import React, { useState } from 'react';
import { Eye, EyeOff, Route as RouteIcon, Search, Edit, Trash } from 'lucide-react';
import { Button } from '../../../../../../../components/common/Button';

export const RoutesTab = ({ routes, map, visibilityState, onVisibilityChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [routeVisibility, setRouteVisibility] = useState({});

  // Initialize route visibility
  React.useEffect(() => {
    if (routes.length > 0) {
      const initialVisibility = {};
      routes.forEach(route => {
        initialVisibility[route.id] = true;
      });
      setRouteVisibility(initialVisibility);
    }
  }, [routes]);

  // Toggle route visibility
  const toggleRouteVisibility = (routeId) => {
    setRouteVisibility(prev => ({
      ...prev,
      [routeId]: !prev[routeId]
    }));
  };

  // Filter routes based on search term
  const filteredRoutes = React.useMemo(() => {
    if (!searchTerm) return routes;
    
    return routes.filter(route => 
      route.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [routes, searchTerm]);

  // Center map on route
  const centerOnRoute = (route) => {
    if (map && route.trackPoints && route.trackPoints.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      route.trackPoints.forEach(point => {
        bounds.extend({
          lat: point.latitude,
          lng: point.longitude
        });
      });
      
      map.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 }
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search routes..."
          className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-400"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
      </div>

      {/* Routes List */}
      <div className="space-y-2">
        {filteredRoutes.map(route => {
          const isVisible = routeVisibility[route.id] || false;
          const routeColor = route.color || '#3B82F6';
          
          return (
            <div 
              key={route.id}
              className="bg-gray-800 rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div 
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => centerOnRoute(route)}
                >
                  <span 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: routeColor }}
                  />
                  <span className="text-white font-medium">{route.name || `Route ${route.id.slice(-8)}`}</span>
                </div>
                <button
                  onClick={() => toggleRouteVisibility(route.id)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  {isVisible ? (
                    <Eye className="w-4 h-4 text-blue-400" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
              
              <div className="text-gray-400 text-sm">
                {route.trackPoints?.length} points • {(route.distance / 1000).toFixed(1)} km
              </div>
            </div>
          );
        })}

        {filteredRoutes.length === 0 && (
          <div className="text-center py-4 text-gray-400">
            {searchTerm ? 'No routes match your search' : 'No routes available'}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutesTab;